<script setup lang="ts">
import type { PropType, ShallowRef } from 'vue';
import { computed, onMounted, ref, shallowRef, unref, watch } from 'vue';

import { prettyNums2, revalidateElement, toFixed10 } from '#public/commons-global';

import InputControl from './input-control.vue';
import { useInputPrettyNumberRaw } from './input-pretty-number';

const props = defineProps({
    modelValue: { type: [String, Number, null] as PropType<string | number | null | undefined>, required: false, default: undefined },
    precision: { type: [String, Number, Array] as PropType<string | number | [number, number] | undefined>, required: false, default: undefined },
});
const emit = defineEmits<{
    (event: 'update:modelValue', value: string): void;
}>();

const prettyNumberRaw = useInputPrettyNumberRaw();
const focused = ref(false);
const localValue = ref('');
const pendingUserSyncValue = ref<string | null>(null);
const focusInitialDisplayValue = ref('');
const focusInitialEditableValue = ref('');
const inputApi = shallowRef<{ input: ShallowRef<HTMLInputElement | undefined> } | undefined>();
const inputElement = computed(() => unref(unref(inputApi)?.input));

function setRawDomValue(value: string | null, revalidate?: boolean) {
    const _inputElement = unref(inputElement);
    if(_inputElement) {
        prettyNumberRaw.setRawDomValue(_inputElement, value);
        if(revalidate) revalidateElement(_inputElement);
    }
}

function normalizeModelValue(value: string | number | null | undefined): string {
    if(value === null || value === undefined) {
        return '';
    }
    return String(value);
}

function getPrecision(raw: unknown): number | undefined {
    const parsed = Number(raw);
    if(Number.isNaN(parsed) || !Number.isFinite(parsed)) {
        return undefined;
    }
    const precision = Math.trunc(parsed);
    if(precision < 0) {
        return undefined;
    }
    return precision;
}

function getPrecisionConfig() {
    const raw = props.precision;
    if(raw === undefined || raw === null || raw === '') {
        return undefined;
    }
    if(Array.isArray(raw)) {
        if(raw.length < 2) {
            return undefined;
        }
        const minRaw = getPrecision(raw[0]);
        const maxRaw = getPrecision(raw[1]);
        if((minRaw === undefined) || (maxRaw === undefined)) {
            return undefined;
        }
        const min = Math.min(minRaw, maxRaw);
        const max = Math.max(minRaw, maxRaw);
        return { min, max, fixed: false as const };
    }
    const fixed = getPrecision(raw);
    if(fixed === undefined) {
        return undefined;
    }
    return { min: fixed, max: fixed, fixed: true as const };
}

function applyMinMaxPrecision(canonical: string, min: number, max: number): string {
    const n = Number(canonical);
    if(Number.isNaN(n) || !Number.isFinite(n)) {
        return canonical;
    }
    const rounded = toFixed10(n, max);
    const [intPart0, fracPart0 = ''] = rounded.split('.');
    const intPart = intPart0;
    let fracPart = fracPart0;
    while((fracPart.length > min) && fracPart.endsWith('0')) {
        fracPart = fracPart.slice(0, -1);
    }
    if(fracPart.length < min) {
        fracPart = fracPart.padEnd(min, '0');
    }
    return fracPart ? `${intPart}.${fracPart}` : (intPart ?? '');
}

function getDecimalIndex(value: string): number {
    const hasComma = value.includes(',');
    const hasDot = value.includes('.');
    if(!hasComma && !hasDot) {
        return -1;
    }
    if(hasComma && hasDot) {
        return Math.max(value.lastIndexOf(','), value.lastIndexOf('.'));
    }
    if(hasComma) {
        return value.lastIndexOf(',');
    }

    // thousands-only localized string (e.g. 200.000), not a decimal value
    if(/^\d{1,3}(\.\d{3})+$/.test(value)) {
        return -1;
    }

    return value.lastIndexOf('.');
}

function sanitizeTypingValue(value: string): string {
    let cleaned = value.replace(/[^\d,.-]/g, '');
    const negative = cleaned.includes('-');
    cleaned = cleaned.replace(/-/g, '');

    const decimalIndex = getDecimalIndex(cleaned);
    let intPart = '';
    let fracPart = '';
    if(decimalIndex >= 0) {
        intPart = cleaned.slice(0, decimalIndex).replace(/[,.]/g, '');
        fracPart = cleaned.slice(decimalIndex + 1).replace(/[,.]/g, '');
        cleaned = `${intPart},${fracPart}`;
    } else {
        cleaned = cleaned.replace(/[,.]/g, '');
    }

    return (negative ? '-' : '') + cleaned;
}

function toCanonical(value: string | number | null | undefined, finalize: boolean, applyPrecision = true): string | null {
    if(value === null || value === undefined || value === '') {
        return null;
    }

    let source = String(value).trim();
    if(!source) {
        return null;
    }

    source = source.replace(/[^\d,.-]/g, '');
    const negative = source.includes('-');
    source = source.replace(/-/g, '');
    if(!source) {
        return null;
    }

    const precision = getPrecisionConfig();
    const decimalIndex = getDecimalIndex(source);
    let intPart = '';
    let fracPart = '';

    if(decimalIndex >= 0) {
        intPart = source.slice(0, decimalIndex).replace(/[,.]/g, '');
        fracPart = source.slice(decimalIndex + 1).replace(/[,.]/g, '');
    } else {
        intPart = source.replace(/[,.]/g, '');
    }

    if(!intPart && !fracPart) {
        return null;
    }

    if(!intPart) {
        intPart = '0';
    }

    if(applyPrecision && (precision !== undefined) && fracPart.length > precision.max) {
        fracPart = finalize
            ? String(toFixed10(Number(`${(negative ? '-' : '') + intPart}.${fracPart}`), precision.max)).split('.')[1] || ''
            : fracPart.slice(0, precision.max);
    }

    let canonical = (negative ? '-' : '') + intPart + (fracPart ? `.${fracPart}` : '');
    if(applyPrecision && (precision !== undefined) && finalize) {
        const n = Number(canonical);
        if(Number.isNaN(n) || !Number.isFinite(n)) {
            return null;
        }
        canonical = precision.fixed
            ? toFixed10(n, precision.max)
            : applyMinMaxPrecision(canonical, precision.min, precision.max);
    }

    const parsed = Number(canonical);
    if(Number.isNaN(parsed) || !Number.isFinite(parsed)) {
        return null;
    }

    return canonical;
}

// canonical values use '.' as decimal separator and have no grouping separator,
// so they must not be re-parsed through toCanonical (which would mistake a
// 3-digit decimal fraction like "2.000" for a thousands-separated integer)
function isCanonicalNumber(value: string): boolean {
    return /^-?\d+(\.\d+)?$/.test(value);
}

function formatLocalized(value: string | number | null | undefined): string {
    if(value === null || value === undefined || value === '') {
        return '';
    }
    if(isCanonicalNumber(String(value))) {
        return String(prettyNums2(String(value), true) ?? '');
    }
    const canonical = toCanonical(value, true);
    if(canonical === null) {
        return '';
    }
    return String(prettyNums2(canonical, true) ?? '');
}

function formatLocalizedIgnorePrecision(value: string | number | null | undefined): string {
    if(value === null || value === undefined || value === '') {
        return '';
    }
    if(isCanonicalNumber(String(value))) {
        return String(prettyNums2(String(value), true) ?? '');
    }
    const canonical = toCanonical(value, true, false);
    if(canonical === null) {
        return '';
    }
    return String(prettyNums2(canonical, true) ?? '');
}

function emitFromUser(value: string) {
    pendingUserSyncValue.value = value;
    emit('update:modelValue', value);
}

function onInputValue(value: string) {
    localValue.value = value;
    const canonical = toCanonical(value, false) ?? '';
    setRawDomValue(canonical);
    emitFromUser(canonical);
}

function onFocus() {
    focused.value = true;
    focusInitialDisplayValue.value = localValue.value;
    focusInitialEditableValue.value = sanitizeTypingValue(localValue.value);
    localValue.value = focusInitialEditableValue.value;
    setRawDomValue(toCanonical(localValue.value, false, false) ?? '');
}

function onBlur() {
    focused.value = false;
    const domValue = unref(inputElement)?.value ?? localValue.value;
    const currentEditable = sanitizeTypingValue(domValue);
    localValue.value = currentEditable;
    const hasDirtyInputChars = domValue !== currentEditable;
    if((currentEditable === focusInitialEditableValue.value) && !hasDirtyInputChars) {
        localValue.value = focusInitialDisplayValue.value;
        return;
    }

    const canonical = toCanonical(localValue.value, true);
    localValue.value = formatLocalized(canonical);
    setRawDomValue(canonical ?? '', true);
    emitFromUser(canonical ?? '');
}

onMounted(() => {
    prettyNumberRaw.ensureValHookInstalled();
    setRawDomValue(toCanonical(props.modelValue, false, false) ?? '');
});

watch(() => [props.modelValue, props.precision] as const, ([value]) => {
    if(focused.value) {
        return;
    }

    const normalized = normalizeModelValue(value);
    if((pendingUserSyncValue.value !== null) && (normalized === pendingUserSyncValue.value)) {
        localValue.value = formatLocalized(value);
    } else {
        localValue.value = formatLocalizedIgnorePrecision(value);
    }
    setRawDomValue(toCanonical(value, false, false) ?? '');
    pendingUserSyncValue.value = null;
}, { immediate: true });

</script>
<template>
    <InputControl
        ref="inputApi"
        :data-pretty-number-raw="true"
        data-autofill-disabled="true"
        data-autoprettify-disabled="true"
        type="text"
        :model-value="localValue"
        @update:model-value="onInputValue"
        @focus="onFocus"
        @blur="onBlur"
    />
</template>
